import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LOGO_URL } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("admin.login.success"));
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error || t("admin.login.fail"));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-giva-navy px-5 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {t("admin.login.back")}
        </Link>
        <div className="rounded-3xl border border-white/10 bg-white p-8 shadow-2xl sm:p-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Giva Solutions" className="h-11 w-11 rounded-xl bg-black object-contain" />
            <div>
              <h1 className="font-display text-2xl font-bold text-giva-navy">{t("admin.login.title")}</h1>
              <p className="text-sm text-giva-text2">{t("admin.login.subtitle")}</p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-medium text-giva-text">{t("admin.login.email")}</label>
              <input
                required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 w-full rounded-xl border border-giva-line bg-giva-bg px-4 py-3 text-base outline-none transition-colors focus:border-giva-primary"
                placeholder="admin@givasolutions.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-giva-text">{t("admin.login.password")}</label>
              <input
                required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 w-full rounded-xl border border-giva-line bg-giva-bg px-4 py-3 text-base outline-none transition-colors focus:border-giva-primary"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading} data-testid="login-submit-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-giva-primary px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-giva-primaryHover disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("admin.login.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
